import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Drawer,
  InputAdornment,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import TextFieldCDS from '@sharedCR/coreDesignSystem/form/TextFieldCDS';
import {
  AreaIcon,
  ArrowCheckIcon,
  BankIcon,
  BathFilledIcon,
  BedIcon,
  BuildingIcon,
  CancelIcon,
  ClockIcon,
  CopyIcon,
  DateIcon,
  EmailFilledIcon,
  FlagFilledIcon,
  GavelIcon,
  HomeIcon,
  InfoFilledIcon,
  LineChartIcon,
  LocationFilledIcon,
  PeopleIcon,
  SearchIcon,
  TagFilledIcon,
  TagOutlinedIcon,
  TerrainFilledIcon,
  TrendUpIcon,
} from '@sharedCR/coreDesignSystem/icons';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize, LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { formatNumberToCurrencyString } from 'utils/number';

import {
  getPropertyEnrichmentAction,
  resetPropertyEnrichmentAction,
  updateReapiPropertyIdAction,
} from 'store/loanApplications/actions';
import { selectPropertyEnrichmentData } from 'store/loanApplications/selectors';

import { PropertyAndLoanTestIds } from '../PropertyAndLoan.testIds';

import {
  AUCTION_DETAIL_FIELDS,
  FORECLOSURE_FIELDS,
  MLS_HISTORY_FIELDS,
  MORTGAGE_FIELDS,
  SALE_FIELDS,
  SECTION_CONFIGS,
} from './PropertyEnrichmentDrawer.config';
import {
  arrayItemStyles,
  bodyStyles,
  closeButtonStyles,
  contentWrapperStyles,
  copyIconStyles,
  drawerStyles,
  emptyStateStyles,
  fieldLabelStyles,
  fieldRowStyles,
  fieldValueCopyWrapperStyles,
  fieldValueStyles,
  headerStyles,
  highlightStyles,
  loaderWrapperStyles,
  metricCardStyles,
  metricIconStyles,
  metricLabelStyles,
  metricsGridStyles,
  metricValueStyles,
  noResultsStyles,
  paperStyles,
  progressStyles,
  searchInputStyles,
  sectionContentStyles,
  sectionCountBadgeStyles,
  sectionHeaderStyles,
  sectionIconStyles,
  sectionStyles,
  sectionTitleRowStyles,
  stickyToolbarStyles,
  toggleAllButtonStyles,
} from './PropertyEnrichmentDrawer.styles';

const slideProps = { timeout: 300 };
const paperProps = { sx: paperStyles };

const KEY_METRICS = [
  {
    label: 'Est. Value',
    getValue: d => d?.estimatedValue,
    format: formatNumberToCurrencyString,
    icon: TagOutlinedIcon,
  },
  { label: 'Property Type', getValue: d => d?.propertyType, icon: HomeIcon },
  { label: 'Year Built', getValue: d => d?.yearBuilt, icon: DateIcon },
  { label: 'Sq Ft', getValue: d => d?.squareFeet?.toLocaleString(), icon: AreaIcon },
  { label: 'Bedrooms', getValue: d => d?.bedrooms, icon: BedIcon },
  { label: 'Bathrooms', getValue: d => d?.bathrooms, icon: BathFilledIcon },
];

const searchAdornment = (
  <InputAdornment position="start">
    <SearchIcon size={IconSize[20]} />
  </InputAdornment>
);

const searchInputProps = { startAdornment: searchAdornment };

const COPY_TOOLTIP_TEXT = 'Click to copy';
const SNACKBAR_AUTO_HIDE = 1500;
const SCROLL_DELAY_MS = 350;

const SECTION_ICONS = {
  Valuation: TrendUpIcon,
  'Property Details': HomeIcon,
  'Tax & Assessment': BankIcon,
  'Lot Info': TerrainFilledIcon,
  Ownership: PeopleIcon,
  Location: LocationFilledIcon,
  'Property Address': LocationFilledIcon,
  'Mailing Address': EmailFilledIcon,
  Flags: FlagFilledIcon,
  'MLS Status': BuildingIcon,
  Demographics: LineChartIcon,
  'Linked Properties': InfoFilledIcon,
  'Last Sale': TagFilledIcon,
  'Sale History': ClockIcon,
  'Current Mortgages': BankIcon,
  'Mortgage History': ClockIcon,
  'Foreclosure History': GavelIcon,
  'Auction Detail': GavelIcon,
  'MLS History': ClockIcon,
};

const highlightText = (text, query) => {
  if (!query || !text) {
    return text;
  }

  const strText = String(text);
  const lowerText = strText.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const matchIndex = lowerText.indexOf(lowerQuery);

  if (matchIndex < 0) {
    return strText;
  }

  const before = strText.slice(0, matchIndex);
  const match = strText.slice(matchIndex, matchIndex + query.length);
  const after = strText.slice(matchIndex + query.length);

  return (
    <>
      {before}
      <Box component="span" sx={highlightStyles}>
        {match}
      </Box>
      {after}
    </>
  );
};

const renderBooleanChip = value => {
  if (value === 'Yes') {
    return <LabelCDS size={LabelSize.S} title="Yes" variant={LabelVariant.GREEN} />;
  }

  if (value === 'No') {
    return <LabelCDS size={LabelSize.S} title="No" variant={LabelVariant.RED} />;
  }

  return null;
};

const FieldRow = memo(({ label, value, searchQuery, onCopy }) => {
  const handleCopy = useCallback(() => {
    onCopy(String(value));
  }, [onCopy, value]);

  if (_.isNil(value) || value === '') {
    return null;
  }

  const strValue = String(value);
  const isBooleanChip = strValue === 'Yes' || strValue === 'No';

  return (
    <Box sx={fieldRowStyles}>
      <Typography sx={fieldLabelStyles} variant="regular14">
        {highlightText(label, searchQuery)}
      </Typography>
      <Tooltip placement="top" title={COPY_TOOLTIP_TEXT}>
        <Box sx={fieldValueCopyWrapperStyles} onClick={handleCopy}>
          {isBooleanChip ? (
            renderBooleanChip(strValue)
          ) : (
            <Typography sx={fieldValueStyles} variant="regular14">
              {highlightText(strValue, searchQuery)}
            </Typography>
          )}
          <Box sx={copyIconStyles}>
            <CopyIcon size={IconSize[14]} />
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
});

FieldRow.displayName = 'FieldRow';

FieldRow.propTypes = {
  label: PropTypes.string.isRequired,
  onCopy: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const getFieldDisplayValue = ({ getValue, format }, data) => {
  const rawValue = getValue(data);

  return format ? format(rawValue) : rawValue;
};

const countPopulatedFields = (fields, data) =>
  fields.reduce((count, fieldConfig) => {
    const value = getFieldDisplayValue(fieldConfig, data);

    return !_.isNil(value) && value !== '' ? count + 1 : count;
  }, 0);

const filterFields = (fields, data, query) => {
  if (!query) {
    return fields;
  }

  const lowerQuery = query.toLowerCase();

  return fields.filter(fieldConfig => {
    const { label } = fieldConfig;
    const value = getFieldDisplayValue(fieldConfig, data);

    return (
      label.toLowerCase().includes(lowerQuery) ||
      (!_.isNil(value) && String(value).toLowerCase().includes(lowerQuery))
    );
  });
};

const renderFields = (fields, data, searchQuery, onCopy) =>
  fields.map(fieldConfig => {
    const displayValue = getFieldDisplayValue(fieldConfig, data);

    return (
      <FieldRow
        key={fieldConfig.label}
        label={fieldConfig.label}
        searchQuery={searchQuery}
        value={displayValue}
        onCopy={onCopy}
      />
    );
  });

const renderArraySection = (items, fields, searchQuery, onCopy) => {
  if (!items?.length) {
    return null;
  }

  return items.map((item, index) => (
    <Box key={`${item.lender || item.date || index}`} sx={arrayItemStyles}>
      <Typography sx={fieldLabelStyles} variant="regular12">
        #{index + 1}
      </Typography>
      {renderFields(fields, item, searchQuery, onCopy)}
    </Box>
  ));
};

const CollapsibleSection = memo(
  ({ title, fieldCount, children, isExpanded, sectionRef, onToggle }) => {
    const handleClick = useCallback(() => onToggle(title), [onToggle, title]);
    const SectionIcon = SECTION_ICONS[title];

    return (
      <Box ref={sectionRef} sx={sectionStyles}>
        <Box sx={sectionHeaderStyles} onClick={handleClick}>
          <Box sx={sectionTitleRowStyles}>
            {!!SectionIcon && (
              <Box sx={sectionIconStyles}>
                <SectionIcon size={IconSize[18]} />
              </Box>
            )}
            <Typography variant="medium14">{title}</Typography>
            {!_.isNil(fieldCount) && (
              <Typography sx={sectionCountBadgeStyles} variant="regular14">
                {fieldCount}
              </Typography>
            )}
          </Box>
          <ArrowCheckIcon direction={isExpanded ? IconDirection.UP : IconDirection.DOWN} />
        </Box>
        <Collapse in={isExpanded}>
          <Box sx={sectionContentStyles}>{children}</Box>
        </Collapse>
      </Box>
    );
  },
);

CollapsibleSection.displayName = 'CollapsibleSection';

CollapsibleSection.propTypes = {
  children: PropTypes.node.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  fieldCount: PropTypes.number,
  sectionRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.object })]),
};

const MetricCard = memo(({ icon: Icon, label, value }) => {
  if (_.isNil(value) || value === '') {
    return null;
  }

  return (
    <Box sx={metricCardStyles}>
      {!!Icon && (
        <Box sx={metricIconStyles}>
          <Icon size={IconSize[20]} />
        </Box>
      )}
      <Typography sx={metricLabelStyles} variant="regular14">
        {label}
      </Typography>
      <Typography sx={metricValueStyles} variant="regular14">
        {String(value)}
      </Typography>
    </Box>
  );
});

MetricCard.displayName = 'MetricCard';

MetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const PropertyEnrichmentDrawer = ({ address, isOpen, propertyId, reapiPropertyId, onClose }) => {
  const dispatch = useDispatch();
  const enrichmentData = useSelector(selectPropertyEnrichmentData);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const sectionRefs = useRef({});

  const getSectionRef = useCallback(
    title => el => {
      sectionRefs.current[title] = el;
    },
    [],
  );

  const canFetch = !_.isNil(reapiPropertyId) || !_.isNil(address);

  const handleCopyValue = useCallback(text => {
    navigator.clipboard.writeText(text);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  const handleFetch = useCallback(async () => {
    setIsLoading(true);

    try {
      const result = await dispatch(getPropertyEnrichmentAction({ reapiPropertyId, address }));

      const providerPropertyId = result?.payload?.providerPropertyId;

      if (_.isNil(reapiPropertyId) && providerPropertyId && propertyId) {
        await dispatch(
          updateReapiPropertyIdAction({
            propertyId,
            reapiPropertyId: providerPropertyId,
          }),
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, reapiPropertyId, address, propertyId]);

  useEffectSafeStrictMode(() => {
    if (isOpen && canFetch && _.isNil(enrichmentData)) {
      handleFetch();
    }
  }, [isOpen, canFetch, enrichmentData, handleFetch]);

  const handleClose = useCallback(() => {
    dispatch(resetPropertyEnrichmentAction());
    setSearchQuery('');
    setExpandedSections({});
    onClose();
  }, [dispatch, onClose]);

  const handleSearchChange = useCallback(
    e => {
      const query = e.target.value;
      setSearchQuery(query);

      if (!query || _.isNil(enrichmentData)) {
        return;
      }

      const lowerQuery = query.toLowerCase();
      const autoExpanded = {};

      SECTION_CONFIGS.forEach(({ title, fields }) => {
        const hasMatch = fields.some(fieldConfig => {
          const { label } = fieldConfig;
          const value = getFieldDisplayValue(fieldConfig, enrichmentData);

          return (
            label.toLowerCase().includes(lowerQuery) ||
            (!_.isNil(value) && String(value).toLowerCase().includes(lowerQuery))
          );
        });

        if (hasMatch) {
          autoExpanded[title] = true;
        }
      });

      const extraSections = [
        { title: 'Last Sale', data: enrichmentData.lastSale, fields: SALE_FIELDS },
        {
          title: 'Auction Detail',
          data: enrichmentData.auctionDetail,
          fields: AUCTION_DETAIL_FIELDS,
        },
      ];

      extraSections.forEach(({ title, data, fields }) => {
        if (!data) {
          return;
        }

        const hasMatch = fields.some(fieldConfig => {
          const { label } = fieldConfig;
          const value = getFieldDisplayValue(fieldConfig, data);

          return (
            label.toLowerCase().includes(lowerQuery) ||
            (!_.isNil(value) && String(value).toLowerCase().includes(lowerQuery))
          );
        });

        if (hasMatch) {
          autoExpanded[title] = true;
        }
      });

      const arraySections = [
        { title: 'Sale History', items: enrichmentData.saleHistory, fields: SALE_FIELDS },
        {
          title: 'Current Mortgages',
          items: enrichmentData.currentMortgages,
          fields: MORTGAGE_FIELDS,
        },
        {
          title: 'Mortgage History',
          items: enrichmentData.mortgageHistory,
          fields: MORTGAGE_FIELDS,
        },
        {
          title: 'Foreclosure History',
          items: enrichmentData.foreclosureHistory,
          fields: FORECLOSURE_FIELDS,
        },
        { title: 'MLS History', items: enrichmentData.mlsHistory, fields: MLS_HISTORY_FIELDS },
      ];

      arraySections.forEach(({ title, items, fields }) => {
        if (!items?.length) {
          return;
        }

        const hasMatch = items.some(item =>
          fields.some(fieldConfig => {
            const { label } = fieldConfig;
            const value = getFieldDisplayValue(fieldConfig, item);

            return (
              label.toLowerCase().includes(lowerQuery) ||
              (!_.isNil(value) && String(value).toLowerCase().includes(lowerQuery))
            );
          }),
        );

        if (hasMatch) {
          autoExpanded[title] = true;
        }
      });

      setExpandedSections(autoExpanded);
    },
    [enrichmentData],
  );

  const handleToggleSection = useCallback(sectionTitle => {
    setExpandedSections(prev => {
      const willExpand = !prev[sectionTitle];
      const next = { ...prev, [sectionTitle]: willExpand };

      if (willExpand) {
        setTimeout(() => {
          sectionRefs.current[sectionTitle]?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }, SCROLL_DELAY_MS);
      }

      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    const allSections = {};

    SECTION_CONFIGS.forEach(({ title }) => {
      allSections[title] = true;
    });

    allSections['Last Sale'] = true;
    allSections['Sale History'] = true;
    allSections['Current Mortgages'] = true;
    allSections['Mortgage History'] = true;
    allSections['Foreclosure History'] = true;
    allSections['Auction Detail'] = true;
    allSections['MLS History'] = true;

    setExpandedSections(allSections);
  }, []);

  const handleCollapseAll = useCallback(() => {
    setExpandedSections({});
  }, []);

  const metrics = useMemo(() => {
    if (_.isNil(enrichmentData)) {
      return [];
    }

    return KEY_METRICS.map(({ label, getValue, format, icon }) => {
      const raw = getValue(enrichmentData);

      if (_.isNil(raw) || raw === '') {
        return null;
      }

      const value = format ? format(raw) : raw;

      return { icon, label, value };
    }).filter(Boolean);
  }, [enrichmentData]);

  const allSectionData = useMemo(() => {
    if (_.isNil(enrichmentData)) {
      return [];
    }

    const sections = SECTION_CONFIGS.map(({ title, fields }) => {
      const filtered = filterFields(fields, enrichmentData, searchQuery);
      const totalCount = countPopulatedFields(fields, enrichmentData);

      return {
        title,
        fields: filtered,
        data: enrichmentData,
        totalCount,
        type: 'fields',
      };
    }).filter(s => s.totalCount > 0);

    const arraySections = [
      {
        title: 'Last Sale',
        condition: !!enrichmentData.lastSale,
        fields: SALE_FIELDS,
        data: enrichmentData.lastSale,
        type: 'fields',
      },
      {
        title: 'Sale History',
        condition: !!enrichmentData.saleHistory?.length,
        fields: SALE_FIELDS,
        items: enrichmentData.saleHistory,
        type: 'array',
      },
      {
        title: 'Current Mortgages',
        condition: !!enrichmentData.currentMortgages?.length,
        fields: MORTGAGE_FIELDS,
        items: enrichmentData.currentMortgages,
        type: 'array',
      },
      {
        title: 'Mortgage History',
        condition: !!enrichmentData.mortgageHistory?.length,
        fields: MORTGAGE_FIELDS,
        items: enrichmentData.mortgageHistory,
        type: 'array',
      },
      {
        title: 'Foreclosure History',
        condition: !!enrichmentData.foreclosureHistory?.length,
        fields: FORECLOSURE_FIELDS,
        items: enrichmentData.foreclosureHistory,
        type: 'array',
      },
      {
        title: 'Auction Detail',
        condition: !!enrichmentData.auctionDetail,
        fields: AUCTION_DETAIL_FIELDS,
        data: enrichmentData.auctionDetail,
        type: 'fields',
      },
      {
        title: 'MLS History',
        condition: !!enrichmentData.mlsHistory?.length,
        fields: MLS_HISTORY_FIELDS,
        items: enrichmentData.mlsHistory,
        type: 'array',
      },
    ];

    const activeArraySections = arraySections
      .filter(s => s.condition)
      .map(({ title, fields, data, items, type }) => {
        if (type === 'array') {
          return {
            title,
            fields,
            items,
            totalCount: items?.length || 0,
            type: 'array',
          };
        }

        const filtered = filterFields(fields, data, searchQuery);
        const totalCount = countPopulatedFields(fields, data);

        if (!totalCount) {
          return null;
        }

        return {
          title,
          fields: filtered,
          data,
          totalCount,
          type: 'fields',
        };
      })
      .filter(Boolean);

    return [...sections, ...activeArraySections];
  }, [enrichmentData, searchQuery]);

  const hasVisibleSections = useMemo(
    () => allSectionData.some(s => (s.type === 'array' ? !!s.items?.length : !!s.fields.length)),
    [allSectionData],
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={loaderWrapperStyles}>
          <CircularProgress sx={progressStyles} />
        </Box>
      );
    }

    if (!canFetch) {
      return (
        <Box sx={emptyStateStyles}>
          <Typography variant="regular14">No property ID or address available</Typography>
        </Box>
      );
    }

    if (_.isNil(enrichmentData)) {
      return (
        <Box sx={emptyStateStyles}>
          <Typography variant="regular14">No enrichment data available</Typography>
        </Box>
      );
    }

    return (
      <>
        {!!metrics.length && (
          <Box sx={metricsGridStyles}>
            {metrics.map(({ icon, label, value }) => (
              <MetricCard icon={icon} key={label} label={label} value={value} />
            ))}
          </Box>
        )}

        <Box sx={stickyToolbarStyles}>
          <TextFieldCDS
            InputProps={searchInputProps}
            ariaLabel="Search fields..."
            dataTestId={PropertyAndLoanTestIds.PROPERTY_ENRICHMENT_DRAWER_SEARCH_INPUT}
            name="propertyEnrichmentSearch"
            placeholder="Search fields..."
            size="small"
            sx={searchInputStyles}
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <Button
            data-testid={PropertyAndLoanTestIds.PROPERTY_ENRICHMENT_DRAWER_EXPAND_ALL_BUTTON}
            size="small"
            sx={toggleAllButtonStyles}
            variant="text"
            onClick={handleExpandAll}
          >
            Expand All
          </Button>
          <Button
            data-testid={PropertyAndLoanTestIds.PROPERTY_ENRICHMENT_DRAWER_COLLAPSE_ALL_BUTTON}
            size="small"
            sx={toggleAllButtonStyles}
            variant="text"
            onClick={handleCollapseAll}
          >
            Collapse All
          </Button>
        </Box>

        {!hasVisibleSections && (
          <Box sx={noResultsStyles}>
            <Typography variant="regular14">No fields match &quot;{searchQuery}&quot;</Typography>
          </Box>
        )}

        {allSectionData.map(section => {
          const hasContent =
            section.type === 'array' ? !!section.items?.length : !!section.fields.length;

          if (!hasContent) {
            return null;
          }

          return (
            <CollapsibleSection
              fieldCount={section.totalCount}
              isExpanded={!!expandedSections[section.title]}
              key={section.title}
              sectionRef={getSectionRef(section.title)}
              title={section.title}
              onToggle={handleToggleSection}
            >
              {section.type === 'array'
                ? renderArraySection(section.items, section.fields, searchQuery, handleCopyValue)
                : renderFields(section.fields, section.data, searchQuery, handleCopyValue)}
            </CollapsibleSection>
          );
        })}
      </>
    );
  };

  return (
    <Drawer
      PaperProps={paperProps}
      SlideProps={slideProps}
      anchor="right"
      data-testid={PropertyAndLoanTestIds.PROPERTY_ENRICHMENT_DRAWER}
      open={isOpen}
      sx={drawerStyles}
      onClose={handleClose}
    >
      <Box sx={contentWrapperStyles}>
        <Box sx={headerStyles}>
          <Typography variant="bold16">Property Details</Typography>
          <Button
            data-testid={PropertyAndLoanTestIds.PROPERTY_ENRICHMENT_DRAWER_CLOSE_BUTTON}
            sx={closeButtonStyles}
            onClick={handleClose}
          >
            <CancelIcon size={IconSize[20]} />
          </Button>
        </Box>
        <Box sx={bodyStyles}>{renderContent()}</Box>
      </Box>
      <Snackbar
        autoHideDuration={SNACKBAR_AUTO_HIDE}
        message="Copied to clipboard"
        open={snackbarOpen}
        onClose={handleSnackbarClose}
      />
    </Drawer>
  );
};

PropertyEnrichmentDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  address: PropTypes.string,
  propertyId: PropTypes.string,
  reapiPropertyId: PropTypes.string,
};

export default memo(PropertyEnrichmentDrawer);
